"use client";

import { c as _c } from "react/compiler-runtime";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { startTransition, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useAuth } from "@/providers/auth";
import { DEFAULT_HIDDEN_COLUMN_IDS, sanitizeHiddenColumnIds, withColumnVisibility } from "../components/table/column-visibility";
type PreferencesResponse = {
  poolsTable: {
    hiddenColumnIds: string[];
  };
};
const SAVE_DEBOUNCE_MS = 450;
async function fetchPreferences(): Promise<string[]> {
  const response = await fetch("/api/user/preferences");
  if (!response.ok) {
    if (response.status === 401) return [...DEFAULT_HIDDEN_COLUMN_IDS];
    throw new Error("Failed to fetch table preferences");
  }
  const data = (await response.json()) as PreferencesResponse;
  return sanitizeHiddenColumnIds(data.poolsTable?.hiddenColumnIds);
}
export function usePoolsTablePreferences(canEditColumns) {
  const $ = _c(31);
  const {
    user
  } = useAuth();
  const userId = user?.id;
  const persistEnabled = !!userId && canEditColumns;
  const queryClient = useQueryClient();
  const [storedHiddenColumnIds, setStoredHiddenColumnIds] = useState(_temp);
  const [saveStatus, setSaveStatus] = useState(null);
  const saveTimer = useRef(null);
  const appliedUserId = useRef(null);
  const dirtyRef = useRef(false);
  let t0;
  if ($[0] !== userId) {
    t0 = ["table-preferences", userId];
    $[0] = userId;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  let t1;
  if ($[2] !== persistEnabled || $[3] !== t0) {
    t1 = {
      queryKey: t0,
      queryFn: fetchPreferences,
      enabled: persistEnabled,
      staleTime: 300000
    };
    $[2] = persistEnabled;
    $[3] = t0;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  const query = useQuery(t1);
  let t2;
  let t3;
  if ($[5] !== persistEnabled || $[6] !== query.data || $[7] !== query.isSuccess || $[8] !== userId) {
    t2 = () => {
      if (!persistEnabled) {
        if (appliedUserId.current !== null || dirtyRef.current) {
          appliedUserId.current = null;
          dirtyRef.current = false;
          startTransition(() => {
            setStoredHiddenColumnIds([...DEFAULT_HIDDEN_COLUMN_IDS]);
            setSaveStatus(null);
          });
        }
        return;
      }
      if (!query.isSuccess || appliedUserId.current === userId || dirtyRef.current) {
        return;
      }
      appliedUserId.current = userId;
      startTransition(() => {
        setStoredHiddenColumnIds(query.data);
      });
    };
    t3 = [persistEnabled, query.data, query.isSuccess, userId];
    $[5] = persistEnabled;
    $[6] = query.data;
    $[7] = query.isSuccess;
    $[8] = userId;
    $[9] = t2;
    $[10] = t3;
  } else {
    t2 = $[9];
    t3 = $[10];
  }
  useEffect(t2, t3);
  let t4;
  if ($[11] !== canEditColumns || $[12] !== storedHiddenColumnIds) {
    t4 = canEditColumns ? sanitizeHiddenColumnIds(storedHiddenColumnIds, true) : [...DEFAULT_HIDDEN_COLUMN_IDS];
    $[11] = canEditColumns;
    $[12] = storedHiddenColumnIds;
    $[13] = t4;
  } else {
    t4 = $[13];
  }
  const hiddenColumnIds = t4;
  let t5;
  if ($[14] !== persistEnabled || $[15] !== queryClient || $[16] !== userId) {
    t5 = next => {
      if (!persistEnabled) {
        return;
      }
      if (saveTimer.current) {
        clearTimeout(saveTimer.current);
      }
      setSaveStatus("Saving\u2026");
      saveTimer.current = setTimeout(() => {
        fetch("/api/user/preferences", {
          method: "PATCH",
          headers: {
            "Content-Type": "application/json"
          },
          body: JSON.stringify({
            poolsTable: {
              hiddenColumnIds: next
            }
          })
        }).then(async response => {
          if (!response.ok) {
            throw new Error("Failed to save table preferences");
          }
          const data = (await response.json()) as PreferencesResponse;
          queryClient.setQueryData(["table-preferences", userId], data.poolsTable.hiddenColumnIds);
          setSaveStatus("Saved");
        }).catch(() => {
          setSaveStatus("Couldn\u2019t save. Try again.");
        });
      }, SAVE_DEBOUNCE_MS);
    };
    $[14] = persistEnabled;
    $[15] = queryClient;
    $[16] = userId;
    $[17] = t5;
  } else {
    t5 = $[17];
  }
  const persist = t5;
  let t6;
  if ($[18] !== canEditColumns || $[19] !== persist) {
    t6 = (id, visible) => {
      if (!canEditColumns) {
        return;
      }
      dirtyRef.current = true;
      setStoredHiddenColumnIds(current => {
        const next_0 = withColumnVisibility(current, id, visible, true);
        persist(next_0);
        return next_0;
      });
    };
    $[18] = canEditColumns;
    $[19] = persist;
    $[20] = t6;
  } else {
    t6 = $[20];
  }
  const setColumnVisible = t6;
  let t7;
  if ($[21] !== canEditColumns || $[22] !== persist) {
    t7 = () => {
      if (!canEditColumns) {
        return;
      }
      dirtyRef.current = true;
      const next_1 = [...DEFAULT_HIDDEN_COLUMN_IDS];
      setStoredHiddenColumnIds(next_1);
      persist(next_1);
    };
    $[21] = canEditColumns;
    $[22] = persist;
    $[23] = t7;
  } else {
    t7 = $[23];
  }
  const resetColumns = t7;
  let t8;
  let t9;
  if ($[24] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = () => () => {
      if (saveTimer.current) {
        clearTimeout(saveTimer.current);
      }
    };
    t9 = [];
    $[24] = t8;
    $[25] = t9;
  } else {
    t8 = $[24];
    t9 = $[25];
  }
  useEffect(t8, t9);
  let t10;
  if ($[26] !== hiddenColumnIds || $[27] !== resetColumns || $[28] !== saveStatus || $[29] !== setColumnVisible) {
    t10 = {
      hiddenColumnIds,
      saveStatus,
      setColumnVisible,
      resetColumns
    };
    $[26] = hiddenColumnIds;
    $[27] = resetColumns;
    $[28] = saveStatus;
    $[29] = setColumnVisible;
    $[30] = t10;
  } else {
    t10 = $[30];
  }
  return t10;
}
function _temp() {
  return [...DEFAULT_HIDDEN_COLUMN_IDS];
}